<template>
  <div class="pt-1">
    <course-create-modal v-if="$can('roster.course.create')" :create-course-active.sync="createCourseActive" @refetch-data="refetchData" />

    <course-edit-modal
      v-if="$can('roster.course.update')"
      :edit-course-active.sync="editCourseActive"
      :course-data="editCourseData"
      @refetch-data="refetchData"
    />

    <div class="content-px mb-1">
      <b-row>
        <b-col cols="12">
          <div class="d-flex align-items-center justify-content-between w-100">
            <div class="d-flex align-items-center justify-content-start flex-wrap flex-sm-nowrap" style="gap: 0.675rem">
              <b-form-input v-model="searchQuery" class="drop-shadow-default" placeholder="Search..." style="max-width: 28rem" />
              <course-list-filters
                :visibility-filter.sync="visibilityFilter"
                :status-filter.sync="statusFilter"
                :visibility-options="visibilityOptions"
                :status-options="statusOptions"
              />
            </div>

            <b-button
              v-if="$can('roster.course.create')"
              variant="primary"
              @click="newCourse"
              class="ml-1"
              style="line-height: 1rem; font-size: 1rem"
            >
              <feather-icon icon="PlusCircleIcon" size="16" class="mr-25" />
              <span class="text-nowrap">New Course</span>
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Table Container Card -->
    <div no-body class="mb-0 table-no-top">
      <b-table
        ref="refCourseListTable"
        class="position-relative mb-0"
        :items="fetchCourseList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        @row-contextmenu="onRowContextMenu"
      >
        <!-- Column: Course -->
        <template #cell(name)="data">
          <div class="d-flex align-items-center" style="gap: 0.625rem">
            <b-avatar
              size="36"
              :src="data.item.avatar"
              :text="avatarText(data.item.name)"
              :variant="`light-info`"
              :to="{ name: 'apps-roster-course-view', params: { id: data.item.id } }"
              rounded
            />
            <div class="d-flex flex-column" style="gap: 0">
              <b-link
                class="font-weight-bold text-nowrap text-white mb-0"
                :to="{ name: 'apps-roster-course-view', params: { id: data.item.id } }"
                style="font-size: 1.25rem; line-height: 1.75rem"
              >
                {{ data.item.name }}
              </b-link>
              <!-- <small class="text-muted" style="font-size: 1.05rem; line-height: 1.35rem">0 Sections</small> -->
              <small class="text-muted" style="font-size: 1.05rem; line-height: 1.35rem"
                >{{ data.item.sectionCounts.total }} Sections</small
              >
            </div>
          </div>
        </template>

        <!-- Column: Live Sessions -->
        <template #cell(live_sections)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize" :class="data.value ? 'text-success' : 'text-muted'">{{
              data.value ? `${data.value} Section${data.value > 1 ? 's' : ''}` : 'None'
            }}</span>
          </div>
        </template>
        <!-- Column: Complete Sessions -->
        <template #cell(completed_sections)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize" :class="data.value ? 'text-white' : 'text-muted'">{{
              data.value ? `${data.value} Section${data.value > 1 ? 's' : ''}` : 'None'
            }}</span>
          </div>
        </template>

        <!-- Column: Visibility -->
        <template #cell(visibility)="data">
          <b-badge :variant="`light-${resolveCourseVisibilityVariant(data.item.visibility)}`" class="text-capitalize">
            {{ data.item.visibility.split('_').join(' ') }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge :variant="`${resolveCourseStatusVariant(data.item.status)}`" class="text-capitalize">
            {{ data.item.status.split('_').join(' ') }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="primary" class="dropdown-edit-button">
            <template #button-content>
              <div class="p-50">
                <feather-icon icon="MoreHorizontalIcon" size="21" />
              </div>
            </template>

            <b-dropdown-item v-if="$can('roster.course.update')" @click="editCourse(data.item)">
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Edit</span>
                <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('roster.course.get')"
              @click="$router.push({ name: 'apps-roster-course-view', params: { id: data.item.id } })"
            >
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>View</span>
                <feather-icon icon="EyeIcon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-50">
        <b-row>
          <b-col cols="12" md="3" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="mb-0 text-muted">{{ dataMeta.of }} Total Entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" md="9" class="d-flex align-items-center justify-content-center justify-content-sm-end mt-1 mt-sm-0">
            <div class="mr-2 vue-select-up">
              <span class="mb-0 mr-75">Rows Per Page</span>
              <v-select
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
                :open="true"
              />
            </div>
            <span class="mr-2 ml-lg-50 mb-0">Page {{ currentPage }} of {{ totalPageCount }}</span>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCourses"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-sm-0 ml-lg-50"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </div>

    <vue-context ref="courseListMenu" class="mw-8" v-slot="{ data }">
      <li v-if="data">
        <a v-if="$can('roster.course.update')" class="d-flex align-items-center justify-content-between" @click="editCourse(data.item)">
          <span>Edit</span>
          <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
        </a>
        <a
          v-if="$can('roster.course.get')"
          class="d-flex align-items-center justify-content-between"
          @click="$router.push({ name: 'apps-roster-course-view', params: { id: data.item.id } })"
        >
          <span>View</span>
          <feather-icon icon="EyeIcon" size="15" class="ml-50 text-muted" />
        </a>
      </li>
    </vue-context>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import { ref, onUnmounted } from '@vue/composition-api';
import { avatarText } from '@core/utils/filter';
import CourseListFilters from './CourseListFilters.vue';
import useCourses from '../useCourses';
import rosterStoreModule from '../../../../../store/roster/rosterStoreModule';
import VueContext from 'vue-context';
import CourseEditModal from '../edit/CourseEditModal.vue';
import CourseCreateModal from '../create/CourseCreateModal.vue';

export default {
  components: {
    CourseListFilters,
    CourseCreateModal,
    CourseEditModal,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,

    vSelect,
    VueContext,
  },
  methods: {
    newCourse() {
      this.createCourseActive = true;
    },
    editCourse(data) {
      this.editCourseData = data;
      this.editCourseActive = true;
    },
    onRowContextMenu(item, index, event) {
      this.$refs.courseListMenu.open(event, { item });
      event.preventDefault();
    },
  },
  mounted() {
    this.$root.$on('fireRootEventN', this.newCourse);
  },
  beforeDestroy() {
    this.$root.$off('fireRootEventN', this.newCourse);
  },
  setup() {
    const editCourseData = ref({});
    const editCourseActive = ref(false);
    const createCourseActive = ref(false);

    const {
      fetchCourseList,
      tableColumns,
      perPage,
      currentPage,
      totalCourses,
      totalPageCount,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refCourseListTable,
      refetchData,

      // UI
      resolveCourseRoleVariant,
      resolveCourseRoleIcon,
      resolveCourseVisibilityVariant,
      resolveCourseStatusVariant,

      visibilityOptions,
      statusOptions,

      // Extra Filters
      visibilityFilter,
      statusFilter,
    } = useCourses();

    return {
      fetchCourseList,
      tableColumns,
      perPage,
      currentPage,
      totalPageCount,
      totalCourses,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refCourseListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveCourseRoleVariant,
      resolveCourseRoleIcon,
      resolveCourseVisibilityVariant,
      resolveCourseStatusVariant,

      visibilityOptions,
      statusOptions,

      // Extra Filters
      visibilityFilter,
      statusFilter,

      editCourseData,
      editCourseActive,
      createCourseActive,
    };
  },
  metaInfo() {
    return {
      title: 'Courses',
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>
